import type { MerchantProduct as ApiMerchantProduct } from '../types'

/**
 * Find first available variant image from any variant option
 */
export function findFirstVariantImage(variantOptions: ApiMerchantProduct['variantOptions']): string | undefined {
  if (!variantOptions?.length) {
    return undefined
  }

  for (const variantOption of variantOptions) {
    if (variantOption.optionList?.length) {
      for (const option of variantOption.optionList) {
        if (option.image && option.image.trim()) {
          return option.image
        }
      }
    }
  }

  return undefined
}

/**
 * Resolve variant image by matching tier_variation to variant_options
 */
export function resolveVariantImage(
  combination: any,
  variantOptions: ApiMerchantProduct['variantOptions']
): string | undefined {
  if (!combination.tierVariation || !variantOptions?.length) {
    return undefined
  }

  // Try to match tier_variation values with variant option names
  for (const tierVariation of combination.tierVariation) {
    for (const [attributeName, attributeValue] of Object.entries(tierVariation)) {
      // Find matching variant option by checking option names
      for (const variantOption of variantOptions) {
        if (variantOption.optionList?.length) {
          for (const option of variantOption.optionList) {
            // Match option name with tier variation value
            if (option.option && typeof attributeValue === 'string' && 
                option.option.toLowerCase() === attributeValue.toLowerCase()) {
              if (option.image && option.image.trim()) {
                return option.image
              }
            }
          }
        }
      }
    }
  }

  // Fallback: try tier_index mapping as backup
  if (combination.tierIndex) {
    for (let tierLevel = 0; tierLevel < combination.tierIndex.length; tierLevel++) {
      const optionIndex = combination.tierIndex[tierLevel]
      const variantOption = variantOptions.find(opt => opt.index === tierLevel)
      
      // Add bounds checking for optionList array
      if (variantOption && variantOption.optionList && optionIndex < variantOption.optionList.length) {
        const option = variantOption.optionList[optionIndex]
        if (option?.image && option.image.trim()) {
          return option.image
        }
      }
    }
  }

  // Don't use findFirstVariantImage() as fallback to avoid showing wrong images
  return undefined
}

/**
 * Get the best available image with proper fallback priority
 */
export function getBestImage(
  merchantProduct: ApiMerchantProduct
): string | undefined {
  // Priority 1: Main product image
  if (merchantProduct.mainImage) {
    return merchantProduct.mainImage
  }
  
  // Priority 2: First variant with tier-index mapped image
  if (merchantProduct.variantCombinations?.length > 0) {
    for (const variant of merchantProduct.variantCombinations) {
      const variantImage = resolveVariantImage(variant, merchantProduct.variantOptions)
      if (variantImage) {
        return variantImage
      }
    }
  }
  
  // Priority 3: Any available variant image (already handled in resolveVariantImage fallback)
  // This fallback is automatically handled by findFirstVariantImage() inside resolveVariantImage()
  
  return undefined
}